import { Injectable, Renderer2 } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { PATH_TRANSLATIONS, SUPPORTED_LANGS, SupportedLang, toCanonicalPath } from '../config/localized-paths';

@Injectable({
    providedIn: 'root'
})
export class LanguageService {
    private renderer: Renderer2;

    constructor(
        private readonly translate: TranslateService,
        private readonly router: Router
    ) {}

    initialize(renderer: Renderer2): void {
        this.renderer = renderer;
    }

    getTranslation(keys: string | string[]): Observable<any> {
        return this.translate.get(keys);
    }

    /**
     * Toggles the visibility of all .english / .spanish elements to match
     * the given language. Uses direct classList API so it can be called
     * without an initialised Renderer2 (e.g. after router navigation).
     */
    applyLanguageVisibility(lang: string): void {
        document.documentElement.setAttribute('lang', lang);

        const allEnglish = document.querySelectorAll<HTMLElement>('.english');
        const allSpanish = document.querySelectorAll<HTMLElement>('.spanish');

        if (lang === 'es') {
            allEnglish.forEach(el => el.classList.add('hidden'));
            allSpanish.forEach(el => el.classList.remove('hidden'));
        } else {
            allEnglish.forEach(el => el.classList.remove('hidden'));
            allSpanish.forEach(el => el.classList.add('hidden'));
        }
    }

    setLanguage(selectedOption: string): void {
        if (!SUPPORTED_LANGS.includes(selectedOption as SupportedLang)) {
          return;
        }

        this.applyLanguageVisibility(selectedOption);

        if (selectedOption === 'en') {
            this.translate.use('en');
            this.updateRecaptchaLanguage('en');
        } else if (selectedOption === 'es') {
            this.translate.use('es');
            this.updateRecaptchaLanguage('es');
        }

        // Swap the :lang URL segment and translate the path segment to the
        // equivalent in the new language using the PATH_TRANSLATIONS map.
        // Doing a canonical round-trip (localized → canonical → localized)
        // prevents broken URLs once Spanish paths diverge from English.
        const [pathPart, queryString] = this.router.url.split('?');
        const urlParts = pathPart.split('/').filter(Boolean);
        const currentLang = urlParts[0] as SupportedLang;
        const currentPath = urlParts[1] ?? 'home';
        const canonicalPath = toCanonicalPath(currentLang, currentPath);
        urlParts[0] = selectedOption;
        urlParts[1] = (PATH_TRANSLATIONS[selectedOption as SupportedLang] as Record<string, string>)[canonicalPath] ?? canonicalPath;
        const newPath = '/' + urlParts.join('/');
        window.location.href = queryString ? `${newPath}?${queryString}` : newPath;  // OneTrust requires page reload
    }
      
    toggleVisibility(elements: NodeListOf<Element>, hidden: boolean): void {
        elements.forEach(element => {
        if (hidden) {
            this.renderer.addClass(element, 'hidden');
        } else {
            this.renderer.removeClass(element, 'hidden');
        }
        });
    }

    private updateRecaptchaLanguage(language: string): void {
        try {
            const iframe = document.querySelector<HTMLIFrameElement>('re-captcha iframe');
            const currentSrc = iframe?.getAttribute('src');
            if (!iframe || !currentSrc) {
                return;
            }

            const nextSrc = /([?&])hl=[-\w]+/.test(currentSrc)
                ? currentSrc.replace(/([?&])hl=[-\w]+/, `$1hl=${language}`)
                : `${currentSrc}${currentSrc.includes('?') ? '&' : '?'}hl=${language}`;

            iframe.setAttribute('src', nextSrc);
        } catch {
            // reCAPTCHA is not rendered on every page
        }
    }
}
